'use client';

import { c as _c } from "react/compiler-runtime";
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, Layout, Link as LinkIcon, Loader2, Trello, Users } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import useIsElectron from '../../hooks/useIsElectron';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
type Props = {
  closeHandler: () => void;
  spaceSlug: string;
  onImportComplete?: () => void;
};
const TRELLO_AUTH_URL = `https://trello.com/1/authorize?key=${process.env.NEXT_PUBLIC_TRELLO_API_KEY}&name=Factiii&scope=read,account&expiration=never&response_type=token&return_url=`;
const STEPS = [{
  id: 'connect',
  label: 'Connect'
}, {
  id: 'select',
  label: 'Select Board'
}, {
  id: 'preview',
  label: 'Configure'
}] as const;
type Step = (typeof STEPS)[number]['id'];
const TrelloImportModal = t0 => {
  const $ = _c(90);
  const {
    closeHandler,
    spaceSlug,
    onImportComplete
  } = t0;
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importStep, setImportStep] = useState("connect");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [memberMapping, setMemberMapping] = useState(t1);
  const isElectron = useIsElectron();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      queryKey: ["trelloHasToken"],
      queryFn: _temp,
      retry: false
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: tokenStatus,
    refetch: refetchTokenStatus
  } = useQuery(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["trelloBoards"];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = tokenStatus?.hasToken === true;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: _temp2,
      enabled: t4,
      retry: false
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    data: boardsData,
    isLoading: isLoadingBoards,
    refetch: refetchBoards
  } = useQuery(t5);
  let t6;
  let t7;
  if ($[5] !== selectedBoardId) {
    t6 = ["trelloBoardData", selectedBoardId];
    t7 = () => unwrapFromResult(client.trello.getBoardData.query({
      boardId: selectedBoardId
    }));
    $[5] = selectedBoardId;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  const t8 = !!selectedBoardId && importStep === "preview";
  let t9;
  if ($[8] !== t6 || $[9] !== t7 || $[10] !== t8) {
    t9 = {
      queryKey: t6,
      queryFn: t7,
      enabled: t8,
      retry: false
    };
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const {
    data: boardData,
    isLoading: isLoadingBoardData
  } = useQuery(t9);
  let t10;
  let t11;
  if ($[12] !== spaceSlug) {
    t10 = ["spaceMembers", spaceSlug];
    t11 = () => unwrapFromResult(client.spaces.memberlist.query({
      slug: spaceSlug
    }));
    $[12] = spaceSlug;
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  const t12 = importStep === "preview";
  let t13;
  if ($[15] !== t10 || $[16] !== t11 || $[17] !== t12) {
    t13 = {
      queryKey: t10,
      queryFn: t11,
      enabled: t12
    };
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const {
    data: spaceMembersData
  } = useQuery(t13);
  let t14;
  let t15;
  if ($[19] !== refetchBoards || $[20] !== refetchTokenStatus) {
    t14 = () => {
      const handleMessage = async event => {
        if (event.origin !== window.location.origin) {
          return;
        }
        if (event.data?.type === "TRELLO_CONNECTED") {
          await refetchTokenStatus();
          await refetchBoards();
          setImportStep("select");
          toast("Trello account connected successfully!");
        } else {
          if (event.data?.type === "TRELLO_ERROR") {
            toast(event.data.message || "Failed to connect Trello account", "", "error");
          }
        }
      };
      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    };
    t15 = [refetchTokenStatus, refetchBoards];
    $[19] = refetchBoards;
    $[20] = refetchTokenStatus;
    $[21] = t14;
    $[22] = t15;
  } else {
    t14 = $[21];
    t15 = $[22];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[23] !== refetchBoards || $[24] !== refetchTokenStatus) {
    t16 = () => {
      if (!window.electronAPI?.onDeepLink) {
        return;
      }
      const unsubscribe = window.electronAPI.onDeepLink(async url => {
        if (!url.startsWith("factiii://trello-callback")) {
          return;
        }
        const params = new URL(url).searchParams;
        const errorParam = params.get("error");
        if (errorParam) {
          toast(errorParam, "", "error");
          return;
        }
        const token = params.get("token");
        if (!token) {
          toast("No Trello token in callback", "", "error");
          return;
        }
        const [error] = await client.trello.storeToken.mutate({
          token
        });
        if (error) {
          toast("Failed to store Trello token", "", "error");
          return;
        }
        await refetchTokenStatus();
        await refetchBoards();
        setImportStep("select");
        toast("Trello account connected successfully!");
      });
      return unsubscribe;
    };
    t17 = [refetchTokenStatus, refetchBoards];
    $[23] = refetchBoards;
    $[24] = refetchTokenStatus;
    $[25] = t16;
    $[26] = t17;
  } else {
    t16 = $[25];
    t17 = $[26];
  }
  useEffect(t16, t17);
  let t18;
  if ($[27] !== tokenStatus?.hasToken) {
    t18 = () => {
      if (tokenStatus?.hasToken) {
        setImportStep("select");
      } else {
        setImportStep("connect");
      }
    };
    $[27] = tokenStatus?.hasToken;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== tokenStatus) {
    t19 = [tokenStatus];
    $[29] = tokenStatus;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  useEffect(t18, t19);
  let t20;
  if ($[31] !== isElectron) {
    t20 = () => {
      if (isElectron && window.electronAPI?.openExternal) {
        const returnUrl = encodeURIComponent(window.location.origin + "/trello/callback" + "?source=desktop");
        window.electronAPI.openExternal(`${TRELLO_AUTH_URL}${returnUrl}`);
        toast("Complete Trello auth in your browser, then return to the app.");
        return;
      }
      const returnUrl_0 = encodeURIComponent(typeof window !== "undefined" ? window.location.origin + "/trello/callback" : "");
      const left = window.screen.width / 2 - 300;
      const top = window.screen.height / 2 - 350;
      const popup = window.open(`${TRELLO_AUTH_URL}${returnUrl_0}`, "trello-auth", `width=${600},height=${700},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
      if (!popup || popup.closed || typeof popup.closed === "undefined") {
        toast("Please allow popups to connect your Trello account", "", "error");
      }
    };
    $[31] = isElectron;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const handleConnectTrello = t20;
  let t21;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = boardId => {
      setSelectedBoardId(boardId);
      setImportStep("preview");
    };
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  const handleBoardSelect = t21;
  let t22;
  if ($[34] !== closeHandler || $[35] !== memberMapping || $[36] !== onImportComplete || $[37] !== selectedBoardId || $[38] !== spaceSlug) {
    t22 = async () => {
      if (!selectedBoardId) {
        return;
      }
      setIsImporting(true);
      const [error_0] = await client.trello.importBoard.mutate({
        boardId: selectedBoardId,
        spaceSlug,
        memberMapping
      });
      if (!error_0) {
        toast("Successfully imported");
        onImportComplete?.();
        closeHandler();
      }
      setIsImporting(false);
    };
    $[34] = closeHandler;
    $[35] = memberMapping;
    $[36] = onImportComplete;
    $[37] = selectedBoardId;
    $[38] = spaceSlug;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  const handleImport = t22;
  let t23;
  if ($[40] !== importStep) {
    t23 = () => {
      if (importStep === "preview") {
        setImportStep("select");
        setSelectedBoardId(null);
      } else {
        if (importStep === "select") {
          setImportStep("connect");
        }
      }
    };
    $[40] = importStep;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const handleBack = t23;
  const getStepIndex = _temp3;
  let t24;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="flex items-center gap-3"><div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Trello size={24} /></div><div><h2 className="text-xl font-semibold text-textPrimary">Import from Trello</h2><p className="text-sm text-textSecondary">Bring your boards, lists, and cards into Factiii</p></div></div>;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== closeHandler) {
    t25 = <ModalHeader closeHandler={closeHandler}>{t24}</ModalHeader>;
    $[43] = closeHandler;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== importStep) {
    t26 = STEPS.map((step_0, index) => {
      const currentIndex = getStepIndex(importStep);
      const isActive = index === currentIndex;
      const isCompleted = index < currentIndex;
      return <div key={step_0.id} className={`flex items-center ${index < STEPS.length - 1 ? "flex-1" : ""}`}><div className={`flex shrink-0 items-center gap-2 ${isActive ? "text-primary" : isCompleted ? "text-primary" : "text-textSecondary"}`}><div className={`flex size-6 items-center justify-center rounded-full text-xs font-semibold transition-colors ${isActive ? "bg-primary text-white" : isCompleted ? "bg-primary text-white" : "bg-background-secondary text-textSecondary"}`}>{isCompleted ? <Check size={14} /> : index + 1}</div><span className="text-sm font-emphasis">{step_0.label}</span></div>{index < STEPS.length - 1 && <div className="mx-4 h-px w-full flex-1 bg-ds-border-standard" />}</div>;
    });
    $[45] = importStep;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== t26) {
    t27 = <div className="border-b border-ds-border-standard px-6 py-4"><div className="flex w-full items-center">{t26}</div></div>;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== handleConnectTrello || $[50] !== importStep) {
    t28 = importStep === "connect" && <div className="flex h-full flex-col items-center justify-center py-12 text-center"><div className="mb-6 rounded-full bg-primary/10 p-6"><Trello size={48} className="text-primary" /></div><h3 className="mb-3 text-2xl font-semibold text-textPrimary">Connect Trello</h3><p className="mb-8 max-w-md text-textSecondary">Connect your Trello account to securely import your boards. We only request read access to your boards and profile. Your connection is private to your account and is never shared with other users or space members.</p><Button text="Connect with Trello" onClick={handleConnectTrello} variant="PRIMARY" classNames="flex items-center gap-2 px-8 py-3 text-lg" icon={<LinkIcon size={20} />} /><p className="mt-6 text-xs text-textSecondary">By connecting, you agree to Trello's Terms of Service and Privacy Policy. You can disconnect Trello at any time from{" "}<Link href="/settings/connected-apps" className="text-primary hover:underline">{"Settings > Connected Apps"}</Link>.</p></div>;
    $[49] = handleConnectTrello;
    $[50] = importStep;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== boardsData || $[53] !== importStep || $[54] !== isLoadingBoards) {
    t29 = importStep === "select" && <div className="py-4">{isLoadingBoards ? <div className="flex flex-col items-center justify-center py-20"><Loader2 className="mb-4 size-10 animate-spin text-primary" /><p className="text-textSecondary">Loading your Trello boards...</p></div> : boardsData?.boards && boardsData.boards.length > 0 ? <div className="space-y-4"><div className="flex items-center justify-between"><h3 className="text-lg font-semibold text-textPrimary">Select a Board</h3><span className="text-sm text-textSecondary">{boardsData.boards.length} boards found</span></div><div className="grid max-h-[400px] grid-cols-1 gap-3 overflow-y-auto pr-2 sm:grid-cols-2">{boardsData.boards.map(board => <button key={board.id} onClick={() => handleBoardSelect(board.id)} className="group relative flex flex-col items-start justify-between rounded-xl border border-ds-border-standard bg-background-secondary p-4 text-left transition-all hover:border-primary hover:shadow-md"><div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Layout size={20} /></div><div><h4 className="font-semibold text-textPrimary group-hover:text-primary">{board.name}</h4><p className="mt-1 text-xs text-textSecondary">Click to configure import</p></div><div className="absolute right-4 top-4 opacity-0 transition-opacity group-hover:opacity-100"><ArrowRight size={20} className="text-primary" /></div></button>)}</div></div> : <div className="flex flex-col items-center justify-center py-12 text-center"><div className="mb-4 rounded-full bg-background-secondary p-4"><Layout size={32} className="text-textSecondary" /></div><h3 className="text-lg font-semibold text-textPrimary">No Boards Found</h3><p className="text-textSecondary">We couldn't find any boards in your Trello account.</p></div>}</div>;
    $[52] = boardsData;
    $[53] = importStep;
    $[54] = isLoadingBoards;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== boardData || $[57] !== importStep || $[58] !== isLoadingBoardData || $[59] !== memberMapping || $[60] !== spaceMembersData?.users) {
    t30 = importStep === "preview" && <div className="py-4">{isLoadingBoardData ? <div className="flex flex-col items-center justify-center py-20"><Loader2 className="mb-4 size-10 animate-spin text-primary" /><p className="text-textSecondary">Analyzing board structure...</p></div> : boardData ? <div className="space-y-6"><div className="grid grid-cols-1 gap-4 sm:grid-cols-3"><div className="rounded-xl border border-ds-border-standard bg-background-secondary p-4"><div className="mb-2 text-sm text-textSecondary">Board Name</div><div className="font-semibold text-textPrimary">{boardData.board.name}</div></div><div className="rounded-xl border border-ds-border-standard bg-background-secondary p-4"><div className="mb-2 text-sm text-textSecondary">Lists to Import</div><div className="font-semibold text-textPrimary">{boardData.listsCount} Lists</div></div><div className="rounded-xl border border-ds-border-standard bg-background-secondary p-4"><div className="mb-2 text-sm text-textSecondary">Total Cards</div><div className="font-semibold text-textPrimary">{boardData.cardsCount}Cards</div></div></div><div className="rounded-lg border border-primary/20 bg-primary/5 p-4"><div className="flex gap-3"><div className="mt-0.5 text-primary"><Layout size={18} /></div><div><h4 className="text-sm font-semibold text-primary">Import Structure</h4><p className="text-sm text-textSecondary">Trello Lists will become Factiiis (Lists). Trello Cards will become Posts. Descriptions will be added as post content.</p></div></div></div>{boardData.members && boardData.members.length > 0 && <div className="space-y-4"><div className="flex items-center gap-2 border-b border-ds-border-standard pb-2"><Users size={18} className="text-primary" /><h3 className="font-semibold text-textPrimary">Member Mapping</h3></div><p className="text-sm text-textSecondary">Map Trello members to Space members to automatically tag them on imported cards.</p><div className="max-h-[300px] space-y-2 overflow-y-auto pr-2">{boardData.members.map(trelloMember => <div key={trelloMember.id} className="flex items-center justify-between gap-4 rounded-lg border border-ds-border-standard bg-background-secondary p-3 transition-colors hover:border-primary/30"><div className="flex flex-1 items-center gap-3"><div className="flex size-8 items-center justify-center rounded-full bg-background-secondary text-xs font-semibold text-textSecondary">{(trelloMember.fullName || "U").substring(0, 2).toUpperCase()}</div><div className="flex flex-col"><span className="text-sm font-emphasis text-textPrimary">{trelloMember.fullName}</span><span className="text-xs text-textSecondary">@{trelloMember.username}</span></div></div><div className="text-textSecondary"><ArrowRight size={16} /></div><div className="flex-1"><select className="w-full rounded-md border border-ds-border-standard bg-background-primary px-3 py-2 text-sm text-textPrimary focus:border-primary focus:outline-none" value={memberMapping[trelloMember.id] || ""} onChange={e => {
                  const val = e.target.value;
                  setMemberMapping(prev => ({
                    ...prev,
                    [trelloMember.id]: val ? Number(val) : 0
                  }));
                }}><option value="">Do not map</option>{spaceMembersData?.users.map(_temp4)}</select></div></div>)}</div></div>}</div> : <div className="rounded-lg border border-ds-border-standard bg-background-secondary p-6 text-center"><p className="text-textSecondary">Failed to load board data.</p></div>}</div>;
    $[56] = boardData;
    $[57] = importStep;
    $[58] = isLoadingBoardData;
    $[59] = memberMapping;
    $[60] = spaceMembersData?.users;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  let t31;
  if ($[62] !== t28 || $[63] !== t29 || $[64] !== t30) {
    t31 = <ModalContent><div className="min-h-[400px]">{t28}{t29}{t30}</div></ModalContent>;
    $[62] = t28;
    $[63] = t29;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  const t32 = importStep === "connect" || isImporting;
  const t33 = importStep === "connect" ? "invisible" : "";
  let t34;
  if ($[66] !== handleBack || $[67] !== t32 || $[68] !== t33) {
    t34 = <Button text="Back" variant="SECONDARY" borderTransparent={false} onClick={handleBack} disabled={t32} classNames={t33} />;
    $[66] = handleBack;
    $[67] = t32;
    $[68] = t33;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  let t35;
  if ($[70] !== closeHandler || $[71] !== isImporting) {
    t35 = <Button text="Cancel" variant="SECONDARY" borderTransparent={false} onClick={closeHandler} disabled={isImporting} />;
    $[70] = closeHandler;
    $[71] = isImporting;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  let t36;
  if ($[73] !== handleImport || $[74] !== importStep || $[75] !== isImporting || $[76] !== selectedBoardId) {
    t36 = importStep === "preview" && <Button text={isImporting ? "Importing..." : "Start Import"} onClick={handleImport} disabled={isImporting || !selectedBoardId} loading={isImporting} variant="PRIMARY" classNames="min-w-[120px]" />;
    $[73] = handleImport;
    $[74] = importStep;
    $[75] = isImporting;
    $[76] = selectedBoardId;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] !== t35 || $[79] !== t36) {
    t37 = <div className="flex gap-3">{t35}{t36}</div>;
    $[78] = t35;
    $[79] = t36;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== t34 || $[82] !== t37) {
    t38 = <ModalFooter><div className="flex w-full items-center justify-between">{t34}{t37}</div></ModalFooter>;
    $[81] = t34;
    $[82] = t37;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== closeHandler || $[85] !== t25 || $[86] !== t27 || $[87] !== t31 || $[88] !== t38) {
    t39 = <Modal closeHandler={closeHandler} maxWidth={900}>{t25}{t27}{t31}{t38}</Modal>;
    $[84] = closeHandler;
    $[85] = t25;
    $[86] = t27;
    $[87] = t31;
    $[88] = t38;
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  return t39;
};
export default TrelloImportModal;
function _temp() {
  return unwrapFromResult(client.trello.hasToken.query());
}
function _temp2() {
  return unwrapFromResult(client.trello.getBoards.query());
}
function _temp3(step) {
  return STEPS.findIndex(s => s.id === step);
}
function _temp4(user) {
  return <option key={user.id} value={user.id}>{user.name || user.username} (@{user.username})</option>;
}